import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, ShoppingCart, Package, Newspaper, Bell, LogOut, Users,
  Truck, Boxes, ClipboardList, Building2, PillBottle, Menu, X, PackageSearch, Repeat, Warehouse, MessageCircle,
} from "lucide-react";

const MENUS = {
  pharmacist: [
    { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard },
    { to: "/operations", label: "Achats groupés", icon: ShoppingCart },
    { to: "/product-requests", label: "Recherche produit", icon: PackageSearch },
    { to: "/exchanges", label: "Échanges", icon: Repeat },
    { to: "/messages", label: "Messagerie", icon: MessageCircle },
    { to: "/orders", label: "Mes commandes", icon: ClipboardList },
    { to: "/news", label: "Actualités", icon: Newspaper },
    { to: "/notifications", label: "Notifications", icon: Bell },
  ],
  supplier: [
    { to: "/supplier", label: "Tableau de bord", icon: LayoutDashboard },
    { to: "/supplier/operations", label: "Mes opérations", icon: ShoppingCart },
    { to: "/supplier/orders", label: "Commandes reçues", icon: ClipboardList },
    { to: "/product-requests", label: "Demandes produits", icon: PackageSearch },
    { to: "/news", label: "Actualités", icon: Newspaper },
    { to: "/notifications", label: "Notifications", icon: Bell },
  ],
  admin: [
    { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard },
    { to: "/admin/pharmacists", label: "Pharmaciens", icon: Users },
    { to: "/admin/suppliers", label: "Fournisseurs", icon: Building2 },
    { to: "/admin/products", label: "Produits", icon: Boxes },
    { to: "/admin/operations", label: "Achats groupés", icon: ShoppingCart },
    { to: "/admin/orders", label: "Commandes", icon: Truck },
    { to: "/admin/grossistes", label: "Grossistes", icon: Warehouse },
    { to: "/product-requests", label: "Recherche produit", icon: PackageSearch },
    { to: "/messages", label: "Messagerie", icon: MessageCircle },
    { to: "/admin/news", label: "Actualités", icon: Newspaper },
    { to: "/notifications", label: "Notifications", icon: Bell },
  ],
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);

  const menu = MENUS[user?.role] || [];

  useEffect(() => {
    api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
    if (user?.role === "pharmacist" || user?.role === "admin") {
      api.get("/messages/unread-count").then((r) => setMsgUnread(r.data.unread || 0)).catch(() => {});
    }
  }, [location.pathname, user?.role]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const roleLabel = { pharmacist: "Pharmacien", supplier: "Fournisseur", admin: "Administrateur" }[user?.role];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0F172A] text-slate-300 flex flex-col transition-transform duration-300 md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center gap-2.5 px-6 border-b border-white/10">
          <div className="h-9 w-9 rounded-lg bg-indigo-600 grid place-items-center">
            <PillBottle className="h-5 w-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="text-white font-extrabold text-[15px]">PharmaGroup</div>
            <div className="text-[10px] text-slate-400 tracking-wide">B2B TUNISIE</div>
          </div>
          <button className="ml-auto md:hidden text-slate-400" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {menu.map((m) => {
            const active = location.pathname === m.to;
            const Icon = m.icon;
            return (
              <Link
                key={m.to}
                to={m.to}
                data-testid={`nav-${m.to.replace(/\//g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${active ? "bg-indigo-600 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}
              >
                <Icon className="h-4.5 w-4.5 shrink-0" style={{ width: 18, height: 18 }} />
                <span>{m.label}</span>
                {m.to === "/notifications" && unread > 0 && (
                  <span className="ml-auto text-[10px] font-bold bg-red-500 text-white rounded-full h-5 min-w-5 px-1 grid place-items-center">{unread}</span>
                )}
                {m.to === "/messages" && msgUnread > 0 && (
                  <span className="ml-auto text-[10px] font-bold bg-indigo-500 text-white rounded-full h-5 min-w-5 px-1 grid place-items-center" data-testid="nav-messages-badge">{msgUnread}</span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-white/5 hover:text-white transition-colors duration-200"
          >
            <LogOut style={{ width: 18, height: 18 }} /> Déconnexion
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="md:ml-64">
        <header className="h-16 sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-slate-200 px-4 md:px-8 flex items-center justify-between">
          <button className="md:hidden text-slate-700" onClick={() => setOpen(true)} data-testid="open-sidebar-btn"><Menu className="h-6 w-6" /></button>
          <div className="hidden md:block text-sm font-semibold text-slate-400 tracking-wide uppercase">{roleLabel}</div>
          <div className="flex items-center gap-3">
            <Link to="/notifications" className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors duration-200" data-testid="header-notifications">
              <Bell className="h-5 w-5 text-slate-600" />
              {unread > 0 && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" />}
            </Link>
            <div className="flex items-center gap-2.5 pl-3 border-l border-slate-200">
              <div className="h-9 w-9 rounded-full bg-indigo-100 text-indigo-700 grid place-items-center font-bold text-sm">
                {(user?.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="hidden sm:block leading-tight">
                <div className="text-sm font-bold text-slate-900" data-testid="current-user-name">{user?.pharmacy_name || user?.name}</div>
                <div className="text-xs text-slate-400">{user?.email}</div>
              </div>
            </div>
          </div>
        </header>
        <main className="p-4 md:p-8 max-w-7xl mx-auto animate-fade-up">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
